/**
 * PhotoContextMenu Component
 *
 * Right-click context menu for photos with version upload functionality.
 * Feature: 081-photo-versioning
 */

import React, { useRef, useState, useCallback, ReactNode } from 'react';
import * as ContextMenu from '@radix-ui/react-context-menu';
import * as Dialog from '@radix-ui/react-dialog';
import { useUploadPhotoVersion } from '../../hooks/graphql/usePhotoVersionsGraphQL';
import { showSuccessToast, showErrorToast } from '../../utils/toast';
import logger from '../../utils/logger';

interface PhotoContextMenuProps {
  /** The photo's global ID */
  photoId: string;
  /** Whether the current user owns this photo */
  isOwner: boolean;
  /** Children to wrap with the context menu */
  children: ReactNode;
  /** Optional callback when a new version is uploaded successfully */
  onVersionUploaded?: (versionNumber: number) => void;
}

/**
 * Context menu for photos that shows on right-click.
 * Provides options like "Upload new version" for photo owners.
 */
export function PhotoContextMenu({
  photoId,
  isOwner,
  children,
  onVersionUploaded,
}: PhotoContextMenuProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileName, setUploadFileName] = useState('');
  const { uploadPhotoVersion, loading } = useUploadPhotoVersion();

  const handleUploadVersionClick = useCallback(() => {
    // Trigger file input click
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/tiff', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showErrorToast('Invalid file type', 'Please upload a JPEG, PNG, TIFF, or WebP image.');
      return;
    }

    // Validate file size (max 100MB)
    const maxSize = 100 * 1024 * 1024;
    if (file.size > maxSize) {
      showErrorToast('File too large', 'Maximum size is 100MB.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadFileName(file.name);
    logger.info('PhotoContextMenu', 'Starting version upload', {
      photoId,
      filename: file.name,
      fileSize: file.size,
    });

    try {
      const result = await uploadPhotoVersion(photoId, file, {
        onUploadProgress: (progress) => setUploadProgress(progress),
      });

      if (result?.success && result.version) {
        showSuccessToast(`Version ${result.version.versionNumber} uploaded`, 'The new version is now active.');
        onVersionUploaded?.(result.version.versionNumber);
      } else if (result?.errorCode === 'DUPLICATE_HASH') {
        // T031a: Handle duplicate file hash error
        showErrorToast('Duplicate file', result.errorMessage || 'This file is identical to an existing version.');
      } else if (result?.errorCode) {
        showErrorToast('Upload failed', result.errorMessage || 'Failed to upload new version.');
      } else {
        showErrorToast('Upload failed', 'Failed to upload new version. Please try again.');
      }
    } catch (error) {
      logger.error('PhotoContextMenu', 'Version upload failed', { error });
      showErrorToast('Upload failed', 'Failed to upload new version. Please try again.');
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [photoId, uploadPhotoVersion, onVersionUploaded]);

  // If user is not the owner, just render children without context menu
  if (!isOwner) {
    return <>{children}</>;
  }

  return (
    <>
      <ContextMenu.Root>
        <ContextMenu.Trigger asChild>
          {children}
        </ContextMenu.Trigger>

        <ContextMenu.Portal>
          <ContextMenu.Content
            className="min-w-[180px] overflow-hidden rounded-md border border-gray-700 bg-gray-800 p-1 shadow-xl"
          >
            <ContextMenu.Item
              className="relative flex cursor-pointer select-none items-center rounded px-2 py-2 text-sm text-gray-200 outline-none hover:bg-gray-700 focus:bg-gray-700 data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
              onSelect={handleUploadVersionClick}
              disabled={isUploading || loading}
            >
              {isUploading || loading ? (
                <>
                  <svg
                    className="mr-2 h-4 w-4 animate-spin"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Uploading...
                </>
              ) : (
                <>
                  <svg
                    className="mr-2 h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  Upload new version
                </>
              )}
            </ContextMenu.Item>

            <ContextMenu.Separator className="my-1 h-px bg-gray-700" />

            <ContextMenu.Item
              className="relative flex cursor-pointer select-none items-center rounded px-2 py-2 text-sm text-gray-400 outline-none hover:bg-gray-700 focus:bg-gray-700"
              disabled
            >
              <svg
                className="mr-2 h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              View version history
              <span className="ml-2 text-xs text-gray-500">(coming soon)</span>
            </ContextMenu.Item>
          </ContextMenu.Content>
        </ContextMenu.Portal>
      </ContextMenu.Root>

      {/* Hidden file input for version upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/tiff,image/webp"
        className="hidden"
        onChange={handleFileChange}
      />

      {/* Upload progress dialog */}
      <Dialog.Root open={isUploading}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] rounded-lg bg-gray-800 border border-gray-700 p-6 shadow-xl z-50">
            <Dialog.Title className="text-lg font-semibold text-white mb-4">
              Uploading New Version
            </Dialog.Title>
            <Dialog.Description className="text-sm text-gray-400 mb-4">
              {uploadFileName}
            </Dialog.Description>

            {/* Progress bar */}
            <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden mb-2">
              <div
                className="h-full bg-primary transition-all duration-150 ease-out"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>

            {/* Progress text */}
            <div className="text-sm text-gray-300 text-center">
              {uploadProgress < 100 ? (
                <span>Uploading... {uploadProgress}%</span>
              ) : (
                <span>Processing...</span>
              )}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}

export default PhotoContextMenu;
